import { IonApp, IonRouterOutlet } from '@ionic/react';
import { IonReactRouter } from '@ionic/react-router';

/* Core CSS required for Ionic components to work properly */
import '@ionic/react/css/core.css';

/* Basic CSS for apps built with Ionic */
import '@ionic/react/css/normalize.css';
import '@ionic/react/css/structure.css';
import '@ionic/react/css/typography.css';

/* Optional CSS utils that can be commented out */
import '@ionic/react/css/padding.css';
import '@ionic/react/css/float-elements.css';
import '@ionic/react/css/text-alignment.css';
import '@ionic/react/css/text-transformation.css';
import '@ionic/react/css/flex-utils.css';
import '@ionic/react/css/display.css';

/* Theme variables */
import './theme/variables.css';
import './global.less';

import {
  StrictMode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import useVH from 'react-viewport-height';
import { IConfig, IConfigApp, IEnvironment } from './interfaces/environment';
import configJson from './config.json';
import { UserService } from './services/UserService';
import { IRole } from './interfaces/role';
import { PageLoading } from '@ant-design/pro-layout';
import GraphqlService from './services/graphql/GraphqlService';
import { IUser, IUserFirebase } from './interfaces/user';
import { ContextApp } from './contexts/ContextApp';
import AppContent from './AppContent';
import { CustomMessage } from './hooks';
import { EnumsValues } from './enums/EnumsValues';
import { setupIonicReact } from '@ionic/react';
import { IDBPDatabase } from 'idb';
import { IAppSetting, Language } from './interfaces';
import { useTranslation } from 'react-i18next';
import { TParameter, TReturn } from './i18n/i18n.model';
import { StringMap, TOptions } from 'i18next';
import { defaultLanguage } from './i18n/i18n';
import { ConfigProvider } from 'antd';
import esES from 'antd/es/locale/es_ES';

require('dotenv').config();

declare global {
  interface Window {
    firebaseui: any;
  }
}

const App: React.FC = () => {
  setupIonicReact();
  useVH();
  const [configApp] = useState<IConfigApp>(configJson);
  const [user, setUser] = useState<IUser | undefined>();
  const [roles, setRoles] = useState<IRole[]>([]);
  const [functions, setFunctions] = useState<string[]>([]);
  const [environment, setEnvironment] = useState<IEnvironment>();
  const [config, setConfig] = useState<IConfig>();
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingMessage, setLoadingMessage] = useState<string>('');
  const [showLoading, setShowLoading] = useState<boolean>(true);
  const [dataLoaded, setDataLoaded] = useState<boolean>(false);
  const [sessionExpired, setSessionExpired] = useState<boolean>(false);
  const [mainMenuCollapsed, setMainMenuCollapsed] = useState(
    window.innerWidth < 992,
  );
  const [menuBreakpointCollapsed, setMenuBreakpointCollapsed] = useState(
    window.innerWidth < 992,
  );
  const [languages, setLanguages] = useState<Language[]>([]);
  const { authenticate } = UserService();
  const { customRequest, Query, Mutation } = GraphqlService();
  const [userDataFirebase, setUserDataFirebase] =
    useState<IUserFirebase | undefined>(undefined);
  const [pictureProfileUrl, setPictureProfileUrl] = useState<string>('');
  const [show2FAReminder, setShow2FAReminder] = useState<boolean>(false);

  const { showMessageError } = CustomMessage();
  const defaultEnvironmentCode = configJson.default_environment;
  const defaultEnvironment = (
    configJson.environments as {
      [key: string]: IEnvironment;
    }
  )[defaultEnvironmentCode];
  const defaultConfig = configJson.default_config;
  const indexedDatabaseRef = useRef<IDBPDatabase<unknown> | null>(null);
  const [i18nT, i18n] = useTranslation();

  const checkAuth = async (): Promise<any> => {
    setShowLoading(true);
    try {
      const res = await authenticate();
      if (res) {
        let user: IUser = {
          id: res.id,
          its: res.its,
          active: res.active,
          username: res.username,
          roles: res.role,
          is_system_user: res.is_system_user,
          language_id: res.language_id,
          profile_id: res.profile_id,
          profile: res.profile,
          uid: res.uid,
          profile_picture_id: res.profile_picture_id,
          two_factor_auth_active: res.two_factor_auth_active,
          signature_id: res.signature_id,
          validated: res.validated,
          accepted_terms_and_conditions: res.accepted_terms_and_conditions,
          person: res.person,
          last_role_id: res.last_role_id,
          lastAccess: res.lastAccess,
          authenticated: res.authenticated,
        };
        setUser(user);
        setRoles(res.roles);
        if (res.two_factor_auth_active) {
          const validate2FA = localStorage.getItem(
            EnumsValues.LocalStorageKeys.TwoFactorValidated,
          );
          if (!validate2FA) {
            localStorage.setItem(
              EnumsValues.LocalStorageKeys.TwoFactorValidated,
              EnumsValues.TwoFactorValidatedValues.False,
            );
          }
        }

        setFunctions(res.permissions);
      }
    } catch (error: any) {
      showMessageError({
        context: 'App.checkAuth.1',
        error,
      });
    }
    setShowLoading(false);
  };
  const getLanguages = async () => {
    try {
      const data: Language[] = await customRequest({
        query: Query.languages,
      });
      setLanguages(() => data);
      if (!data.find((item) => item.language_code === i18n.language)) {
        i18n.changeLanguage(defaultLanguage);
        i18n.loadLanguages(data.map((item) => item.language_code));
      }
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  const getTitle = async () => {
    try {
      let title: IAppSetting = await customRequest({
        query: Query.getAppSettingByKey,
        variables: {
          input: { key: EnumsValues.SettingNames.PageTitle },
        },
      });
      document.title = title.setting_value
        ? title.setting_value
        : EnumsValues.DefaultValues.Title;
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  };

  const switchRole = useCallback(async () => {
    let role_id: number;
    if (
      user?.last_role_id === EnumsValues.UserRoleSystem.Tenant ||
      user?.last_role_id === EnumsValues.UserRoleSystem.Owner
    ) {
      if (user?.last_role_id === EnumsValues.UserRoleSystem.Tenant) {
        role_id = EnumsValues.UserRoleSystem.Owner;
      } else {
        role_id = EnumsValues.UserRoleSystem.Tenant;
      }
    } else {
      return;
    }
    setLoading(true);
    try {
      await customRequest({
        query: Mutation.changeLastRoleId,
        variables: {
          role_id,
        },
      });
      await checkAuth();
    } catch (error: any) {
      showMessageError({
        context: 'App.switchRole.1',
        error,
      });
    }
    setLoading(false);
  }, [user]);

  const currentLanguage = useMemo(() => {
    switch (i18n.language) {
      // TODO: Descomentar para incluir traducciones en inglés de antd
      // case 'en':
      //   return enUS;
      case 'es':
        return esES;
      default:
        return esES;
    }
  }, [i18n.language]);

  function t<S extends string>(
    p: TParameter<S>,
    options?: TOptions<StringMap>,
  ): TReturn<S> {
    return i18nT(p, { ...options, fallbackLng: 'es' });
  }

  useEffect(() => {
    getLanguages();
    getTitle();
    let token = localStorage.getItem(EnumsValues.LocalStorageKeys.Token);
    if (!user && token) {
      checkAuth();
    } else {
      setShowLoading(() => false);
    }
  }, []);

  return (
    <ContextApp.Provider
      value={{
        configApp,
        user,
        setUser,
        setLoading,
        loading,
        loadingMessage,
        setLoadingMessage,
        setShowLoading,
        showLoading,
        environment: environment || defaultEnvironment,
        setEnvironment,
        roles,
        setRoles,
        functions,
        setFunctions,
        config: config || defaultConfig,
        setConfig,
        dataLoaded,
        setDataLoaded,
        checkAuth,
        mainMenuCollapsed,
        setMainMenuCollapsed,
        menuBreakpointCollapsed,
        setMenuBreakpointCollapsed,
        sessionExpired,
        setSessionExpired,
        languages,
        userDataFirebase,
        setUserDataFirebase,
        pictureProfileUrl,
        setPictureProfileUrl,
        show2FAReminder,
        setShow2FAReminder,
        indexedDatabaseRef,
        t,
        switchRole,
      }}
    >
      <IonApp>
        <ConfigProvider
          locale={currentLanguage}
          theme={{
            token: {
              colorPrimary: '#132C7D',
              colorPrimaryBgHover: '#2b448a',
              fontFamily:
                'Open Sans, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica Neue, Arial,Noto Sans, sans-serif, Apple Color Emoji, Segoe UI Emoji, Segoe UI Symbol,Noto Color Emoji',
              colorTextPlaceholder: '#626262',
              colorBgLayout: '#F3FAFF',
            },
            components: {
              Button: {
                colorBgContainerDisabled: '#B9C0D8',
                colorTextDisabled: '#FFFFFF',
                colorLink: '#132C7D',
                colorBorder: '#132C7D',
                colorText: '#132C7D',
              },
              Layout: {
                colorBgContainer: '#F3FAFF',
                colorBgLayout: '#F3FAFF',
              },
              Menu: {
                colorItemBg: '#F3FAFF',
              },
              InputNumber: {
                colorTextPlaceholder: 'rgba(0, 0, 0, 0.87)',
                colorBorder: '#AAAAAA',
              },
              Input: {
                colorTextPlaceholder: 'rgba(0, 0, 0, 0.87)',
                colorBorder: '#AAAAAA',
              },
              Select: {
                colorTextPlaceholder: 'rgba(0, 0, 0, 0.87)',
                colorBorder: '#AAAAAA',
              },
            },
          }}
        >
          {!showLoading ? (
            <IonReactRouter>
              <IonRouterOutlet>
                <StrictMode>
                  <AppContent />
                </StrictMode>
              </IonRouterOutlet>
            </IonReactRouter>
          ) : (
            <PageLoading />
          )}
        </ConfigProvider>
      </IonApp>
    </ContextApp.Provider>
  );
};

export default App;
